import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SplitPane from 'react-split-pane';
import '../styles/SplitPane.css';
import { 
  Box, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  Typography, 
  Divider, 
  Grid,
  TextField,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { fetchConversations, fetchMessages, sendMessage as sendMessageApi } from '../services/api';
import { socketService } from '../services/socket';
import AddContactModal from './AddContactModal';
import MessageView from './MessageView';
import { debounce } from 'lodash';

// Add HighlightText component at the top of the file
const HighlightText = ({ text, searchQuery, sx = {} }) => {
  if (!searchQuery || searchQuery.length < 3) return <span style={sx}>{text}</span>;
  
  // Process the search query to handle 'all:' prefix
  const searchTerm = searchQuery.toLowerCase().startsWith('all:') 
    ? searchQuery.toLowerCase().slice(4).trim() 
    : searchQuery.toLowerCase();
    
  if (searchTerm.length < 3) return <span style={sx}>{text}</span>;
  
  const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
  return (
    <span style={sx}>
      {parts.map((part, i) => 
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <span 
            key={i} 
            style={{ 
              backgroundColor: 'rgba(248, 151, 28, 1.0)', // FeedTahoe orange with opacity
              color: 'white',
              padding: '0px 3px',
              borderRadius: '2px'
            }}
          >
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

// Move helper functions outside components
const getContactTypeColor = (type) => {
  switch (type) {
    case 'donor':
      return '#015887'; // FITAHO blue
    case 'nonprofit':
      return '#F8971C'; // FITAHO orange
    case 'volunteer':
      return '#4CAF50'; // Green
    case 'other':
      return '#FFACA9'; // Purple for other
    default:
      return '#9E9E9E'; // Gray for unknown
  }
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getContactTypeLabel = (conversation, donors, nonprofits) => {
  if (!conversation) return 'UNKNOWN';
  
  switch (conversation.contactType) {
    case 'donor':
      // For donors, show the organization name if available
      if (conversation.contactId) {
        const donor = donors.find(d => String(d.id) === String(conversation.contactId));
        return donor ? donor.name : 'DONOR';
      }
      return 'DONOR';
    case 'nonprofit':
      // For nonprofits, show the organization name if available
      if (conversation.contactId) {
        const nonprofit = nonprofits.find(n => String(n.id) === String(conversation.contactId));
        return nonprofit ? nonprofit.name : 'NONPROFIT';
      }
      return 'NONPROFIT';
    case 'volunteer':
      return 'VOLUNTEER';
    case 'other':
      return 'OTHER';
    default:
      return 'UNKNOWN';
  }
};

const shouldLabelBeUppercase = (conversation) => {
  // Only use uppercase for volunteer, unknown, and unassigned donor/nonprofit
  if (!conversation) return true;
  
  if (conversation.contactType === 'volunteer' || conversation.contactType === 'unknown') {
    return true;
  }
  
  // For donor/nonprofit, only uppercase if we don't have an organization name
  if (conversation.contactType === 'donor' || conversation.contactType === 'nonprofit') {
    return !conversation.contactId;
  }
  
  return false;
};

// Add a new component for the search info modal
const SearchInfoModal = ({ open, onClose }) => (
  <Dialog 
    open={open} 
    onClose={onClose}
    maxWidth="sm"
  >
    <DialogTitle sx={{ 
      fontFamily: 'Merriweather, Georgia, serif',
      borderBottom: '2px solid',
      borderColor: 'secondary.main',
      pb: 2
    }}>
      Search Tips
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'text.secondary',
        }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box>
          <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
            Default Search
          </Typography>
          <Typography variant="body2" color="text.secondary">
            By default, search looks for matches in:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">Contact names</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Phone numbers</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Contact types</Typography>
          </Box>
        </Box>
        
        <Box>
          <Typography variant="subtitle1" color="secondary" sx={{ mb: 1, fontWeight: 600 }}>
            Advanced Search
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the <code style={{ backgroundColor: '#F5F8FA', padding: '2px 4px', borderRadius: 4 }}>all:</code> prefix to search through all message content.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Example: <code style={{ backgroundColor: '#F5F8FA', padding: '2px 4px', borderRadius: 4 }}>all:delivery</code> will search through all messages for the word "delivery"
          </Typography>
        </Box>

        <Box sx={{ 
          backgroundColor: '#F5F8FA',
          p: 2,
          borderRadius: 1,
          borderLeft: '4px solid',
          borderColor: 'primary.main'
        }}>
          <Typography variant="body2" color="text.secondary">
            Note: Search terms must be at least 3 characters long.
          </Typography>
        </Box>
      </Box>
    </DialogContent>
  </Dialog>
);

// Add a new component for the conversation list panel
const ConversationListPanel = ({ 
  conversations, 
  selectedConversation,
  onConversationClick,
  loading,
  error,
  searchQuery,
  setSearchQuery,
  isMobile,
  donors,
  nonprofits,
  volunteers,
  unreadMessages
}) => {
  const [searchInfoOpen, setSearchInfoOpen] = useState(false);

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ 
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        backgroundColor: 'white',
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        minHeight: '56px',
      }}>
        <TextField
          placeholder="Search conversations..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color={loading ? "disabled" : "action"} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setSearchInfoOpen(true)}
                  size="small"
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 3,
              backgroundColor: '#F5F8FA',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }
          }}
        />
      </Box>
      
      {/* Add the search info modal */}
      <SearchInfoModal 
        open={searchInfoOpen}
        onClose={() => setSearchInfoOpen(false)}
      />
      
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Loading State */}
        {loading && (
          <Box sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <Typography variant="body1" color="text.secondary">
              Loading conversations...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <Typography variant="body1" color="error">
              {error}
            </Typography>
          </Box>
        )}
      
        {/* Conversation List */}
        {!loading && !error && (
          <List sx={{ 
            padding: 0,
            pb: '87px', // Add bottom padding to match message list
            '& .MuiListItem-root': {
              transition: 'all 0.2s',
              borderRadius: 0,
            },
          }}>
            {(conversations || [])
              .filter(conv => {
                if (!searchQuery || searchQuery.length < 3) return true;
                
                const searchLower = searchQuery.toLowerCase();
                
                // Check if this is an "all:" search
                if (searchLower.startsWith('all:')) {
                  const allSearchTerm = searchLower.slice(4).trim(); // Remove 'all:' and trim
                  if (allSearchTerm.length < 3) return true; // Skip if search term is too short
                  
                  // Search in contact name, type, phone number AND message bodies
                  const basicMatch = conv.contactName.toLowerCase().includes(allSearchTerm) ||
                                   conv.phoneNumber.includes(allSearchTerm) ||
                                   (conv.contactType || '').toLowerCase().includes(allSearchTerm);
                  
                  // Search in message bodies
                  const messageMatch = conv.messages?.some(msg => 
                    msg.body.toLowerCase().includes(allSearchTerm)
                  );
                  
                  // Calculate and store match count if we have a match
                  if (basicMatch || messageMatch) {
                    // Only calculate if we haven't already
                    if (conv.matchCount === undefined && conv.messages) {
                      conv.matchCount = conv.messages.filter(msg => 
                        msg.body.toLowerCase().includes(allSearchTerm)
                      ).length;
                    }
                    return true;
                  }
                  return false;
                }
                
                // Default search: only in contact name, type, and phone number
                return conv.contactName.toLowerCase().includes(searchLower) ||
                       conv.phoneNumber.includes(searchQuery) ||
                       (conv.contactType || '').toLowerCase().includes(searchLower);
              })
              .map((conversation) => (
                <React.Fragment key={conversation.phoneNumber}>
                  <ListItem 
                    button 
                    selected={selectedConversation?.phoneNumber === conversation.phoneNumber}
                    onClick={() => onConversationClick(conversation)}
                    sx={{
                      py: 0.4,
                      px: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '& .MuiTypography-root': {
                          color: 'primary.contrastText',
                        },
                        '& .MuiTypography-caption': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                        '& .message-count': {
                          color: '#F8971C !important'
                        }
                      },
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        mr: 1.5, 
                        bgcolor: getContactTypeColor(conversation.contactType),
                        color: 'white',
                        width: 36,
                        height: 36,
                        border: conversation.unread ? '2px solid' : 'none',
                        borderColor: conversation.unread ? getContactTypeColor(conversation.contactType) : 'transparent',
                        boxShadow: conversation.unread ? '0 0 0 2px rgba(255, 255, 255, 0.8)' : 'none',
                      }}
                      src={conversation.profile_url}
                    >
                      {!conversation.profile_url && getInitials(conversation.contactName)}
                    </Avatar>
                    <ListItemText
                      disableTypography
                      primary={
                        <Box component="div" sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          mb: 0.25,
                          position: 'relative',
                          pr: unreadMessages[conversation.phoneNumber] > 0 ? 4 : 0 // Add padding when badge is present
                        }}>
                          <Typography 
                            variant="subtitle1" 
                            component="div"
                            sx={{ 
                              color: 'inherit',
                              fontWeight: conversation.unread ? 600 : 500,
                              fontSize: '0.85rem'
                            }}
                          >
                            <HighlightText 
                              text={conversation.contactName}
                              searchQuery={searchQuery}
                            />
                          </Typography>
                          {unreadMessages[conversation.phoneNumber] > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                minWidth: '20px',
                                height: '20px',
                                borderRadius: '10px',
                                backgroundColor: '#FF3B30',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                padding: '0 6px',
                                boxShadow: '0 2px 4px rgba(255, 59, 48, 0.3)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'translateY(-50%) scale(1.05)',
                                  boxShadow: '0 3px 6px rgba(255, 59, 48, 0.4)'
                                }
                              }}
                            >
                              {unreadMessages[conversation.phoneNumber]}
                            </Box>
                          )}
                          <Typography
                            variant="caption"
                            component="div"
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              backgroundColor: getContactTypeColor(conversation.contactType),
                              color: 'white',
                              fontSize: '0.65rem',
                              fontWeight: 500,
                              textTransform: shouldLabelBeUppercase(conversation) ? 'uppercase' : 'none',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {getContactTypeLabel(conversation, donors, nonprofits)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box component="div">
                          <Typography
                            variant="body2"
                            component="div"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontWeight: conversation.unread ? 500 : 400,
                              color: 'inherit',
                              maxWidth: '100%',
                              mb: 0.25,
                              fontSize: '0.8rem'
                            }}
                          >
                            <HighlightText 
                              text={conversation.lastMessage}
                              searchQuery={searchQuery}
                            />
                          </Typography>
                          <Typography 
                            variant="caption" 
                            component="div"
                            sx={{ 
                              color: selectedConversation?.phoneNumber === conversation.phoneNumber ? 
                                'rgba(255, 255, 255, 0.7)' : 'secondary.main',
                              fontSize: '0.65rem'
                            }}
                          >
                            {formatDistanceToNow(new Date(conversation.lastMessageTime), { addSuffix: true })}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
          </List>
        )}
      </Box>
    </Paper>
  );
};

// Add new constant for local storage key
const UNREAD_MESSAGES_KEY = 'feedtahoe:unread_messages';
const LAST_READ_TIMESTAMP_KEY = 'feedtahoe:last_read_timestamp';

function ConversationList({ 
  isNewConversationOpen, 
  onNewConversationClose, 
  initialPhoneNumber, 
  userName,
  onOpenSettings 
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [conversations, setConversations] = useState([]);
  const [phoneDirectory, setPhoneDirectory] = useState({});
  const [donors, setDonors] = useState([]);
  const [nonprofits, setNonprofits] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMobileList, setShowMobileList] = useState(!initialPhoneNumber);
  const [splitPos, setSplitPos] = useState(
    localStorage.getItem('conversationSplitPos') || '30%'
  );
  const [newConversationData, setNewConversationData] = useState({
    phoneNumber: '',
    name: '',
    message: ''
  });
  const [addContactModalOpen, setAddContactModalOpen] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(UNREAD_MESSAGES_KEY)) || {};
    } catch {
      return {};
    }
  });
  
  const messagesEndRef = React.useRef(null);

  // Effect to handle username changes and initial load
  useEffect(() => {
    if (userName) {
      // Only set last read timestamp if it doesn't exist
      if (!localStorage.getItem(LAST_READ_TIMESTAMP_KEY)) {
        const timestamp = new Date().toISOString();
        localStorage.setItem(LAST_READ_TIMESTAMP_KEY, timestamp);
      }
    }
  }, [userName]);

  // Update the handleNewMessage callback to be more explicit about unread state
  const handleNewMessage = useCallback((message) => {
    if (message.direction === 'incoming') {
      const lastReadTimestamp = localStorage.getItem(LAST_READ_TIMESTAMP_KEY);
      if (lastReadTimestamp && new Date(message.timestamp) > new Date(lastReadTimestamp)) {
        // Don't mark as unread if this is the currently selected conversation
        if (selectedConversation?.phoneNumber !== message.phoneNumber) {
          setUnreadMessages(prev => {
            const newState = {
              ...prev,
              [message.phoneNumber]: (prev[message.phoneNumber] || 0) + 1
            };
            localStorage.setItem(UNREAD_MESSAGES_KEY, JSON.stringify(newState));
            return newState;
          });
          
          setConversations(prev => prev.map(conv => 
            conv.phoneNumber === message.phoneNumber
              ? { ...conv, unread: true }
              : conv
          ));
        }
      }
    }
  }, [selectedConversation?.phoneNumber]);

  // Update the URL effect to be simpler and more focused
  useEffect(() => {
    if (initialPhoneNumber && conversations.length > 0) {
      const conversation = conversations.find(conv => conv.phoneNumber === initialPhoneNumber);
      if (conversation) {
        setSelectedConversation(conversation);
        setShowMobileList(false);
      } else {
        setError(`Conversation with phone number ${initialPhoneNumber} not found`);
      }
    } else {
      setSelectedConversation(null);
      setShowMobileList(true);
    }
  }, [initialPhoneNumber, conversations]);

  // Simplify handleMobileBack to just handle navigation
  const handleMobileBack = useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  // Update handleConversationClick to be simpler
  const handleConversationClick = async (conversation) => {
    try {
      setMessageLoading(true);
      
      // Mark conversation as read in both state and local storage
      const newUnreadMessages = { ...unreadMessages };
      delete newUnreadMessages[conversation.phoneNumber];
      setUnreadMessages(newUnreadMessages);
      localStorage.setItem(UNREAD_MESSAGES_KEY, JSON.stringify(newUnreadMessages));
      
      // Update conversations list
      setConversations(prev => prev.map(conv => 
        conv.phoneNumber === conversation.phoneNumber
          ? { ...conv, unread: false }
          : conv
      ));

      // If we don't have messages, load them
      let messages = conversation.messages;
      if (!messages || messages.length === 0) {
        messages = await loadMessages(conversation.phoneNumber);
      }

      // Set the conversation state with messages
      const conversationToSet = {
        ...conversation,
        unread: false,
        messages
      };

      setSelectedConversation(conversationToSet);
      
      // Handle navigation
      if (isMobile) {
        setShowMobileList(false);
      }
      navigate(`/c/${conversation.phoneNumber}`, { replace: true });

    } catch (err) {
      console.error('Error in handleConversationClick:', err);
      setError('Failed to load messages');
    } finally {
      setMessageLoading(false);
    }
  };

  const loadConversations = async () => {
    try {
      const data = await fetchConversations();
      // Reverse the messages arrays in each conversation to display in chronological order
      const conversationsWithReversedMessages = data.conversations.map(conv => ({
        ...conv,
        messages: conv.messages ? [...conv.messages].reverse() : []
      }));
      setConversations(conversationsWithReversedMessages);
      setPhoneDirectory(data.phoneDirectory);
      setDonors(data.donors);
      setNonprofits(data.nonprofits);
      setVolunteers(data.volunteers);
      setLoading(false);
      return data;
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations');
      setLoading(false);
    }
  };

  const loadMessages = async (phoneNumber) => {
    try {
      setError(null);
      const messages = await fetchMessages(phoneNumber);
      // Messages come from the backend in descending order (newest first)
      // so we need to reverse them for chronological display
      return messages.reverse();
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages');
      return [];
    }
  };

  const sendMessage = async (messageText) => {
    try {
      // Create a temporary message object for immediate UI update
      const tempMessage = {
        id: `temp-${Date.now()}`,
        direction: 'outgoing',
        body: messageText,
        timestamp: new Date().toISOString(),
        userName: userName
      };

      // Optimistically update UI
      setSelectedConversation(prev => ({
        ...prev,
        lastMessage: messageText,
        lastMessageTime: tempMessage.timestamp,
        messages: [...(prev.messages || []), tempMessage]
      }));

      // Update conversations list optimistically
      setConversations(prev => prev.map(conv =>
        conv.phoneNumber === selectedConversation.phoneNumber
          ? {
              ...conv,
              lastMessage: messageText,
              lastMessageTime: tempMessage.timestamp,
              messages: [...(conv.messages || []), tempMessage]
            }
          : conv
      ));

      // Actually send the message
      const sentMessage = await sendMessageApi(selectedConversation.phoneNumber, messageText, userName);
      
      // Replace temp message with real one without triggering a full refresh
      setSelectedConversation(prev => ({
        ...prev,
        lastMessage: sentMessage.body,
        lastMessageTime: sentMessage.timestamp,
        messages: prev.messages.map(msg => 
          msg.id === tempMessage.id ? sentMessage : msg
        )
      }));

      // Update conversations list with real message
      setConversations(prev => prev.map(conv =>
        conv.phoneNumber === selectedConversation.phoneNumber
          ? {
              ...conv,
              lastMessage: sentMessage.body,
              lastMessageTime: sentMessage.timestamp,
              messages: conv.messages.map(msg => 
                msg.id === tempMessage.id ? sentMessage : msg
              )
            }
          : conv
      ));

    } catch (err) {
      console.error('Failed to send message:', err);
      // Revert optimistic updates on error
      setSelectedConversation(prev => ({
        ...prev,
        messages: prev.messages.filter(msg => msg.id !== `temp-${Date.now()}`)
      }));
      setConversations(prev => prev.map(conv =>
        conv.phoneNumber === selectedConversation.phoneNumber
          ? {
              ...conv,
              messages: conv.messages.filter(msg => msg.id !== `temp-${Date.now()}`)
            }
          : conv
      ));
    }
  };

  const findExistingConversation = (phoneNumber) => {
    const formattedNumber = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
    return conversations.find(conv => conv.phoneNumber === formattedNumber);
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const formattedNumber = value.startsWith('+') ? value : `+${value}`;
    const existingConv = findExistingConversation(formattedNumber);
    
    setNewConversationData(prev => ({
      ...prev,
      phoneNumber: formattedNumber,
      name: existingConv ? existingConv.contactName : prev.name
    }));
  };

  const handleStartNewConversation = async () => {
    try {
      const formattedNumber = newConversationData.phoneNumber;
      const existingConv = findExistingConversation(formattedNumber);

      // Send the message using sendMessageApi
      const sentMessage = await sendMessageApi(formattedNumber, newConversationData.message, userName);

      let updatedConversation;
      if (existingConv) {
        // Update existing conversation with new message
        updatedConversation = {
          ...existingConv,
          lastMessage: sentMessage.body,
          lastMessageTime: sentMessage.timestamp,
          messages: existingConv.messages ? [...existingConv.messages, sentMessage] : [sentMessage]
        };

        setConversations(prev => prev.map(conv =>
          conv.phoneNumber === formattedNumber
            ? updatedConversation
            : conv
        ));
      } else {
        // Create new conversation
        updatedConversation = {
          phoneNumber: formattedNumber,
          contactName: newConversationData.name,
          lastMessage: sentMessage.body,
          lastMessageTime: sentMessage.timestamp,
          messages: [sentMessage],
          unread: false
        };
        setConversations(prev => [updatedConversation, ...prev].sort((a, b) => 
          new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
        ));
      }

      // Close dialog and reset form
      onNewConversationClose();
      setNewConversationData({ phoneNumber: '', name: '', message: '' });

      // Navigate to the conversation and show it
      setSelectedConversation(updatedConversation);
      navigate(`/c/${formattedNumber}`);
      if (isMobile) {
        setShowMobileList(false);
      }
    } catch (err) {
      console.error('Failed to start conversation:', err);
    }
  };

  // Add handler for split pane resize
  const handleSplitPaneChange = (newSize) => {
    setSplitPos(newSize);
    localStorage.setItem('conversationSplitPos', newSize);
  };

  // Add back the socket connection effect after the URL change effect
  useEffect(() => {
    loadConversations();
    
    // Connect to socket
    socketService.connect();

    // Set up message handler
    const cleanup = socketService.onMessage((message) => {
      // Skip handling our own sent messages since we already handled them in sendMessage
      if (message.direction === 'outgoing' && message.userName === userName) {
        return;
      }

      // Skip if this is a message we just sent (it will be handled by sendMessage)
      if (selectedConversation?.phoneNumber === message.phoneNumber && 
          message.direction === 'outgoing' && 
          message.body === selectedConversation.lastMessage &&
          message.userName === userName) {
        return;
      }

      // Skip if this is a duplicate message we already have
      const isDuplicate = selectedConversation?.messages?.some(msg => 
        msg.id === message.id || 
        (msg.body === message.body && 
         msg.timestamp === message.timestamp && 
         msg.direction === message.direction)
      );
      if (isDuplicate) {
        return;
      }

      // Track new message if it's incoming
      handleNewMessage(message);

      setConversations(prev => {
        // Find if we already have this conversation
        const existingConvIndex = prev.findIndex(conv => conv.phoneNumber === message.phoneNumber);
        
        if (existingConvIndex >= 0) {
          // Update existing conversation
          const updatedConv = {...prev[existingConvIndex]};
          updatedConv.lastMessage = message.body;
          updatedConv.lastMessageTime = message.timestamp;
          
          // Update messages array if this conversation is currently selected
          if (selectedConversation?.phoneNumber === message.phoneNumber) {
            // Append message to maintain chronological order
            updatedConv.messages = [...(updatedConv.messages || []), message];
          }
          
          // If this is an incoming message and no username, mark as unread
          if (message.direction === 'incoming' && !message.userName) {
            updatedConv.unread = true;
          }

          const newConversations = [...prev];
          newConversations[existingConvIndex] = updatedConv;
          
          // Sort conversations by last message time
          return newConversations.sort((a, b) => 
            new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
          );
        } else {
          // Create new conversation
          const newConv = {
            phoneNumber: message.phoneNumber,
            contactName: message.phoneNumber,
            lastMessage: message.body,
            lastMessageTime: message.timestamp,
            unread: message.direction === 'incoming' && !message.userName,
            messages: [message]
          };
          
          // Add new conversation and sort
          return [...prev, newConv].sort((a, b) => 
            new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
          );
        }
      });

      // If this message belongs to the currently selected conversation,
      // update its messages while maintaining chronological order
      if (selectedConversation?.phoneNumber === message.phoneNumber) {
        setSelectedConversation(prev => ({
          ...prev,
          lastMessage: message.body,
          lastMessageTime: message.timestamp,
          messages: [...(prev.messages || []), message]
        }));
      }
    });

    // Clean up
    return () => {
      cleanup();
      socketService.disconnect();
    };
  }, [userName, selectedConversation?.phoneNumber, handleNewMessage]); // Add handleNewMessage to dependencies

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      overflow: 'hidden',
    }}>
      {isMobile ? (
        <Box sx={{ height: '100%', width: '100%' }}>
          {showMobileList || !selectedConversation ? (
            <ConversationListPanel 
              conversations={conversations}
              selectedConversation={selectedConversation}
              onConversationClick={handleConversationClick}
              loading={loading}
              error={error}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              isMobile={true}
              donors={donors}
              nonprofits={nonprofits}
              volunteers={volunteers}
              unreadMessages={unreadMessages}
            />
          ) : (
            <MessageView
              conversation={selectedConversation}
              messages={selectedConversation?.messages || []}
              onBack={handleMobileBack}
              onSend={sendMessage}
              donors={donors}
              nonprofits={nonprofits}
              isMobile={true}
              onAddContact={() => setAddContactModalOpen(true)}
              searchQuery={searchQuery}
              userName={userName}
              onOpenSettings={onOpenSettings}
              isLoading={messageLoading}
            />
          )}
        </Box>
      ) : (
        <SplitPane
          split="vertical"
          minSize={300}
          maxSize={600}
          size={splitPos}
          onChange={handleSplitPaneChange}
          paneStyle={{ overflow: 'hidden' }}
          style={{ position: 'relative' }}
        >
          <Box sx={{ 
            height: '100%', 
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <ConversationListPanel 
              conversations={conversations}
              selectedConversation={selectedConversation}
              onConversationClick={handleConversationClick}
              loading={loading}
              error={error}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              isMobile={false}
              donors={donors}
              nonprofits={nonprofits}
              volunteers={volunteers}
              unreadMessages={unreadMessages}
            />
          </Box>
          <Box sx={{ height: '100%', overflow: 'hidden' }}>
            {selectedConversation ? (
              <MessageView
                conversation={selectedConversation}
                messages={selectedConversation.messages || []}
                onSend={sendMessage}
                donors={donors}
                nonprofits={nonprofits}
                isMobile={false}
                onAddContact={() => setAddContactModalOpen(true)}
                searchQuery={searchQuery}
                userName={userName}
                onOpenSettings={onOpenSettings}
                isLoading={messageLoading}
              />
            ) : (
              <Box sx={{ 
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#F5F8FA',
              }}>
                <Typography variant="h6" color="text.secondary">
                  Select a conversation
                </Typography>
              </Box>
            )}
          </Box>
        </SplitPane>
      )}

      {/* New Conversation Dialog */}
      <Dialog 
        open={isNewConversationOpen} 
        onClose={onNewConversationClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Merriweather, Georgia, serif' }}>
          {findExistingConversation(newConversationData.phoneNumber) 
            ? 'Continue Conversation' 
            : 'New Conversation'}
          <IconButton
            aria-label="close"
            onClick={onNewConversationClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Phone Number"
              variant="outlined"
              value={newConversationData.phoneNumber}
              onChange={handlePhoneNumberChange}
              margin="normal"
              placeholder="+1234567890"
              helperText={findExistingConversation(newConversationData.phoneNumber) 
                ? "This number already exists in your conversations" 
                : "Enter phone number with country code"}
            />
            <TextField
              fullWidth
              label="Contact Name"
              variant="outlined"
              value={newConversationData.name}
              onChange={(e) => setNewConversationData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              disabled={findExistingConversation(newConversationData.phoneNumber)}
              helperText={findExistingConversation(newConversationData.phoneNumber) 
                ? "Name cannot be changed for existing conversations" 
                : "Enter contact name"}
            />
            <TextField
              fullWidth
              label="Message"
              variant="outlined"
              value={newConversationData.message}
              onChange={(e) => setNewConversationData(prev => ({ ...prev, message: e.target.value }))}
              margin="normal"
              multiline
              rows={8}
              placeholder="Type your message here..."
            />
            {/* Quick Message Templates */}
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Quick welcome messages:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ 
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    borderColor: '#4CAF50',
                    color: '#4CAF50',
                    '&:hover': {
                      borderColor: '#45a049',
                      backgroundColor: 'rgba(74, 175, 80, 0.08)'
                    }
                  }}
                  onClick={() => {
                    const namePrefix = newConversationData.name ? `Hey ${newConversationData.name}, ` : "Hey, ";
                    const welcomeMessage = `${namePrefix}this is FeedTahoe. We're excited to have you join our volunteer team! Here's our volunteer guide to help you get started:\n\nhttps://www.feedtahoe.org/volunteerinfo\n\nWe recommend reviewing it before your first rescue. Feel free to text us here if you have any questions - we're here to help!`;
                    setNewConversationData(prev => ({ ...prev, message: welcomeMessage }));
                  }}
                >
                  🤝 Volunteer Welcome
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ 
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    borderColor: '#015887',
                    color: '#015887',
                    '&:hover': {
                      borderColor: '#014870',
                      backgroundColor: 'rgba(1, 88, 135, 0.08)'
                    }
                  }}
                  onClick={() => {
                    const namePrefix = newConversationData.name ? `Hey ${newConversationData.name}, ` : "Hey, ";
                    const welcomeMessage = `${namePrefix}this is FeedTahoe. Thank you for partnering with us as a donor! Here's our donor information page:\n\nhttps://www.feedtahoe.org/donors\n\nIt explains our food rescue process and what to expect. Please save this number - you can text us here anytime with questions or updates about food donations.`;
                    setNewConversationData(prev => ({ ...prev, message: welcomeMessage }));
                  }}
                >
                  🏪 Donor Welcome
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ 
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    borderColor: '#F8971C',
                    color: '#F8971C',
                    '&:hover': {
                      borderColor: '#e88a19',
                      backgroundColor: 'rgba(248, 151, 28, 0.08)'
                    }
                  }}
                  onClick={() => {
                    const namePrefix = newConversationData.name ? `Hey ${newConversationData.name}, ` : "Hey, ";
                    const welcomeMessage = `${namePrefix}this is FeedTahoe. We're thrilled to partner with you as a nonprofit recipient! Here's our nonprofit partner guide:\n\nhttps://www.feedtahoe.org/nonprofits\n\nIt covers everything you need to know about receiving food donations. Feel free to text us here with any questions about deliveries or scheduling.`;
                    setNewConversationData(prev => ({ ...prev, message: welcomeMessage }));
                  }}
                >
                  🏢 Nonprofit Welcome
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onNewConversationClose}>Cancel</Button>
          <Button 
            onClick={handleStartNewConversation}
            variant="contained" 
            color="secondary"
            startIcon={findExistingConversation(newConversationData.phoneNumber) 
              ? <SendIcon /> 
              : <PersonAddIcon />}
            disabled={!newConversationData.phoneNumber || 
                     (!findExistingConversation(newConversationData.phoneNumber) && !newConversationData.name) || 
                     !newConversationData.message}
          >
            {findExistingConversation(newConversationData.phoneNumber) 
              ? 'Send Message' 
              : 'Start Conversation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add the modal at the end of the component */}
      <AddContactModal
        open={addContactModalOpen}
        onClose={() => setAddContactModalOpen(false)}
        phoneNumber={selectedConversation?.phoneNumber}
        donors={donors}
        nonprofits={nonprofits}
        volunteers={volunteers}
        existingContact={selectedConversation?.contactType && selectedConversation?.contactId ? {
          contactType: selectedConversation.contactType,
          contactName: selectedConversation.contactName,
          contactId: selectedConversation.contactId
        } : null}
        onSave={async (response) => {
          // Reload conversations to get updated contact info
          const data = await loadConversations();
          
          // Find the updated conversation in the new data
          const updatedConversation = data.conversations.find(
            conv => conv.phoneNumber === selectedConversation?.phoneNumber
          );
          
          if (updatedConversation) {
            // Update the selected conversation with all new metadata
            setSelectedConversation(prev => ({
              ...updatedConversation,
              messages: prev.messages // Keep existing messages
            }));
          }
          
          // Close the modal
          setAddContactModalOpen(false);
        }}
      />
    </Box>
  );
}

export default ConversationList; 